// src/pages/Gallery.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Play, Image, ZoomIn, X } from 'lucide-react';

// Import your assets
import photo1 from '../assets/images/first.JPG';
import photo2 from '../assets/images/second.JPG';
import photo3 from '../assets/images/third.JPG';
import photo4 from '../assets/images/fourth.JPG';
import photo5 from '../assets/images/fifth.JPG';
import photo6 from '../assets/images/sixth.JPG';
import photo7 from '../assets/images/seventh.JPG';
import video1 from '../assets/videos/first.MP4';
import video2 from '../assets/videos/second.MP4';
import video3 from '../assets/videos/third.MP4';
// import video4 from '../assets/videos/fourth.MP4';
import video5 from '../assets/videos/fifth.MP4';

const AnimatedSection = ({ children, className = '' }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ y: 60, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const MediaCard = ({ item, type, index, onClick }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ y: 60, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ scale: 1.05 }}
      className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
      onClick={() => onClick(item)}
    >
      {type === 'image' ? (
        <div className="aspect-square relative">
          <img
            src={item.src}
            alt={item.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <ZoomIn
              size={48}
              className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <h3 className="text-white font-semibold text-lg">{item.title}</h3>
            <p className="text-gray-300 text-sm">{item.date}</p>
          </div>
        </div>
      ) : (
        <div className="aspect-video relative">
          <video
            src={item.src}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <Play
              size={48}
              className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <h3 className="text-white font-semibold text-lg">{item.title}</h3>
            <p className="text-gray-300 text-sm">{item.date}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const Gallery = () => {
  const [activeTab, setActiveTab] = useState('photos');
  const [selectedMedia, setSelectedMedia] = useState(null);

  const photos = [
    { id: 1, title: 'Award Ceremony', date: 'March 2024', src: photo1, type: 'image' },
    { id: 2, title: 'Award Ceremony', date: 'February 2024', src: photo2, type: 'image' },
    { id: 3, title: 'Award Ceremony', date: 'February 2024', src: photo3, type: 'image' },
    { id: 4, title: 'Award Ceremony', date: 'February 2024', src: photo4, type: 'image' },
    { id: 5, title: 'Award Ceremony', date: 'February 2024', src: photo5, type: 'image' },
    { id: 6, title: 'Award Ceremony', date: 'February 2024', src: photo6, type: 'image' },
    { id: 7, title: 'Award Ceremony', date: 'February 2024', src: photo7, type: 'image' },
  ];

  const videos = [
    { id: 1, title: 'Award Ceremony', date: 'March 2024', src: video1, type: 'video' },
    { id: 2, title: 'Award Ceremony', date: 'February 2024', src: video2, type: 'video' },
    { id: 3, title: 'Award Ceremony', date: 'February 2024', src: video3, type: 'video' },
   // { id: 4, title: 'Award Ceremony', date: 'February 2024', src: video4, type: 'video' },
    { id: 5, title: 'Award Ceremony', date: 'February 2024', src: video5, type: 'video' },
  ];

  const handleMediaClick = (item) => {
    setSelectedMedia(item);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  return (
    <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      {/* Header */}
      <AnimatedSection className="text-center mb-16 lg:mb-24">
        <h1 className="text-4xl xs:text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-800 mb-6 lg:mb-8">
          Gallery
        </h1>
        <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 max-w-4xl xl:max-w-5xl mx-auto leading-relaxed">
          Explore our visual journey through photos and videos that capture the impact 
          of educational funding and the success stories we've helped create.
        </p>
      </AnimatedSection>

      {/* Tab Navigation */}
      <AnimatedSection className="mb-12 lg:mb-16">
        <div className="flex justify-center">
          <div className="bg-white rounded-2xl p-2 shadow-lg border border-gray-100">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('photos')}
                className={`px-6 py-3 rounded-xl font-semibold text-lg transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'photos'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
                }`}
              >
                <Image size={20} />
                <span>Photos</span>
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-6 py-3 rounded-xl font-semibold text-lg transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'videos'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
                }`}
              >
                <videos size={20} />
                <span>Videos</span>
              </button>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Media Sections */}
      <AnimatePresence mode="wait">
        {activeTab === 'photos' && (
          <motion.div
            key="photos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AnimatedSection>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-8 lg:mb-12 text-center">
                Photo Gallery
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {photos.map((photo, index) => (
                  <MediaCard key={photo.id} item={photo} type="image" index={index} onClick={handleMediaClick} />
                ))}
              </div>
            </AnimatedSection>
          </motion.div>
        )}

        {activeTab === 'videos' && (
          <motion.div
            key="videos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AnimatedSection>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-8 lg:mb-12 text-center">
                Video Gallery
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {videos.map((video, index) => (
                  <MediaCard key={video.id} item={video} type="video" index={index} onClick={handleMediaClick} />
                ))}
              </div>
            </AnimatedSection>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal for Zoom / Video */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          >
            <div className="relative max-w-4xl w-full max-h-full">
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 z-10"
              >
                <X size={24} />
              </button>
              {selectedMedia.type === 'image' ? (
                <img
                  src={selectedMedia.src}
                  alt={selectedMedia.title}
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <video
                  src={selectedMedia.src}
                  className="w-full h-auto rounded-lg shadow-lg"
                  controls
                  autoPlay
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
